import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  FileText, Archive, Calendar, MoreVertical, Eye, Download, Lock, Globe, Building, Upload,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

export const getClassificationBadge = (c) => {
  const s = { open: 'bg-green-100 text-green-800', internal: 'bg-blue-100 text-blue-800', classified: 'bg-red-100 text-red-800' };
  return s[c] || 'bg-gray-100 text-gray-700';
};

export const getClassificationIcon = (c) => {
  if (c === 'open') return <Globe className="h-3 w-3 text-green-600" />;
  if (c === 'internal') return <Building className="h-3 w-3 text-blue-600" />;
  if (c === 'classified') return <Lock className="h-3 w-3 text-red-600" />;
  return null;
};

export const RecordCard = ({ record, icon, color = '#00A3A3', onArchive }) => (
  <Card className={`border-[#E2E6EA] hover:border-[${color}]/40 transition-colors ${record.status === 'archived' ? 'opacity-60' : ''}`} data-testid={`record-card-${record.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-[#F0F2F4] rounded-lg shrink-0">
          {record.status === 'archived' ? <Archive className="h-5 w-5 text-[#A8B1B9]" /> : (icon || <FileText className="h-5 w-5" style={{ color }} />)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-medium text-[#1A1D21] truncate">{record.title}</h4>
            {record.status !== 'archived' && onArchive && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem><Eye className="h-4 w-4 mr-2" />View</DropdownMenuItem>
                  <DropdownMenuItem><Download className="h-4 w-4 mr-2" />Download</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onArchive([record.id])} className="text-amber-600"><Archive className="h-4 w-4 mr-2" />Archive</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {record.classification && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ${getClassificationBadge(record.classification)}`}>
                {getClassificationIcon(record.classification)} {record.classification}
              </span>
            )}
            {record.created_at && (
              <span className="text-[10px] text-[#A8B1B9] flex items-center gap-1"><Calendar className="h-3 w-3" />{format(parseISO(record.created_at), 'MMM d, yyyy')}</span>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const EmptyState = ({ icon: Icon, message, sub }) => (
  <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
    <Icon className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
    <h3 className="text-base font-medium text-[#1A1D21] mb-1">{message}</h3>
    {sub && <p className="text-sm text-[#5A6570]">{sub}</p>}
  </CardContent></Card>
);

export const UploadButton = ({ tab, defaultType = '', defaultGov = '', onOpen }) => (
  <Button onClick={() => onOpen(tab, defaultType, defaultGov)} className="bg-[#00A3A3] hover:bg-[#008585] shrink-0" data-testid={`upload-${tab}-btn`}>
    <Upload className="h-4 w-4 mr-2" />Upload
  </Button>
);
